/**
 * The device half of the compute step: a formula's answer, worked out here.
 *
 * Section 5.7 rule 6, which Section 5.5 inherits: "the device runs the same
 * rule". The rule is `evaluateFormula` in `@alluzo/shared`, imported by this
 * file and by `apps/server/src/formula-fill.ts`, so there is one implementation
 * and not two that must agree.
 *
 * WHY THE PHONE COMPUTES AT ALL. A save is offline first. Without this the
 * Amount on a sale saved on a train would be blank until the next pull, and the
 * day total on the home screen would be short by every row still pending. The
 * server recomputes on sync and the server value wins, exactly as the doc says.
 *
 * WHAT NEVER LEAVES. The outbox carries the TYPED values only. The server
 * refuses a written value on a worked-out field, and it is right to: the recipe
 * is the truth, and a device that could post an answer could post a wrong one.
 * `withoutComputed` is what the push path calls.
 */
import {
  evaluateFormula,
  formulaHopsRead,
  formulaLinesRead,
  formulaOrder,
  type EntryLinesWrite,
  type EntryValues,
  type FormulaSpec,
  type LineWrite,
  type FormulaValues,
  type LinesReader,
  type RecordSchemaResponse,
  formulaOf,
} from '@alluzo/shared';
import { and, eq, inArray, isNull } from 'drizzle-orm';

import { getDb } from '@/lib/db';
import { entryMirror } from '@/lib/db-schema';
import { lineContext, withoutLineRefs, type LineAbove, type RowAbove } from '@/lib/line-above';

/** Every field on this record whose value is worked out from a recipe. */
const formulasOf = (schema: RecordSchemaResponse): readonly { id: string; formula: FormulaSpec }[] =>
  schema.fields.flatMap((field) => {
    const formula = field.type === 'formula' ? formulaOf(field.spec) : null;
    return formula === null ? [] : [{ id: field.id, formula }];
  });

/** True when this record has nothing to work out, which is the common case. */
export const hasFormulas = (schema: RecordSchemaResponse): boolean =>
  formulasOf(schema).length > 0;

/** The chosen row ids a multi_select value holds, in the person's order. */
const chosenIds = (value: unknown): readonly string[] =>
  Array.isArray(value) ? value.filter((one): one is string => typeof one === 'string' && one !== '') : [];

/**
 * The values of a set of mirrored rows, keyed by entry id.
 *
 * One query, whatever the number of hops. A row this device has not pulled yet
 * is simply absent, and the formula that wanted it answers nothing rather than
 * a wrong number.
 */
const valuesForEntryIds = (ids: readonly string[]): Map<string, EntryValues> => {
  const out = new Map<string, EntryValues>();
  if (ids.length === 0) return out;

  const rows = getDb()
    .select({ entryId: entryMirror.entryId, valuesJson: entryMirror.valuesJson })
    .from(entryMirror)
    .where(
      and(
        inArray(entryMirror.entryId, [...ids]),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
      ),
    )
    .all();

  for (const row of rows) {
    try {
      out.set(row.entryId, JSON.parse(row.valuesJson) as EntryValues);
    } catch {
      // A row whose document will not parse is skipped, not thrown. One bad
      // mirror row must never stop a person saving a sale.
    }
  }
  return out;
};

/**
 * What a form of lines hands the fill beside the row's own values (Decisions
 * 6 and 8 of the lines plan), the same two things the server's
 * `FormulaContext` carries.
 */
export type FormulaFillContext = {
  /**
   * Rows a hop may land on that the form itself holds, keyed by entry id: a
   * line's parent and root, which are not in the mirror while the entry is
   * unsaved. A hop to one of them reads the form's values, never the mirror.
   */
  readonly known?: ReadonlyMap<string, FormulaValues>;
  /** The lines directly under this row, by `lines` field id, each already worked out. */
  readonly childLines?: ReadonlyMap<string, readonly FormulaValues[]>;
};

const NO_KNOWN: ReadonlyMap<string, FormulaValues> = new Map();
const NO_LINES: ReadonlyMap<string, readonly FormulaValues[]> = new Map();

/**
 * The same values, with every formula on the record answered.
 *
 * The answers are folded in on top, so a caller cannot accidentally supply one:
 * whatever was passed for a formula field is replaced by what the recipe says.
 *
 * THE ORDER MATTERS, and it is not the order the fields happen to be in. A
 * formula may read another formula on the same row — a Total that reads an
 * Amount that reads the customer's usual rate — so an answer has to be worked
 * out before the formula that reads it, and it has to be fed into the picture
 * that one reads. `formulaOrder` in `@alluzo/shared` decides the order, and
 * `applyFormulas` on the server sorts by the very same function: one
 * implementation, so the two sides cannot disagree about what a row says.
 *
 * A ring answers NOTHING. `checkFormula` refuses one when the field is made, so
 * a ring on a device can only be a spec written by an older build or edited by
 * hand; the fields in it are set to null and the rest are still filled. Half a
 * row of right answers beats a row of guesses.
 */
export const fillFormulas = (
  schema: RecordSchemaResponse,
  values: EntryValues,
  context: FormulaFillContext = {},
): EntryValues => {
  const formulas = formulasOf(schema);
  if (formulas.length === 0) return values;
  const known = context.known ?? NO_KNOWN;
  const childLines = context.childLines ?? NO_LINES;
  // A `lines` field stores nothing on the row: its rows are the lines under
  // it, handed in. A multi_select holds the ids of its chosen rows.
  const linesFields = new Set(schema.fields.filter((field) => field.type === 'lines').map((field) => field.id));

  // Only the ids a hop names, and the rows an `agg` totals a field over, so a
  // record whose formulas reach no other row touches the database not at all.
  // A count reads no field, so it counts the ids and loads nothing: the server
  // counts the same ids.
  const pointed = new Set<string>();
  for (const field of formulas) {
    for (const node of formulaHopsRead(field.formula)) {
      const held = values[node.via];
      if (typeof held === 'string' && held !== '' && !known.has(held)) pointed.add(held);
    }
    for (const read of formulaLinesRead(field.formula)) {
      if (read.fields.length === 0 || linesFields.has(read.lines)) continue;
      for (const id of chosenIds(values[read.lines])) pointed.add(id);
    }
  }
  const rows = valuesForEntryIds([...pointed]);
  const readHop = (entryId: string, fieldId: string): unknown =>
    known.has(entryId) ? known.get(entryId)?.[fieldId] : rows.get(entryId)?.[fieldId];

  const held = new Map<string, FormulaSpec>(
    formulas.map((field) => [field.id, field.formula]),
  );
  const listed = formulas.map((field) => ({ fieldId: field.id, spec: field.formula }));

  const first = formulaOrder(listed);
  const ringed = first.ok ? new Set<string>() : new Set(first.cycle);
  // Whatever is left once the ring is taken out has no ring of its own, so the
  // second pass always answers an order.
  const sorted = first.ok
    ? first
    : formulaOrder(listed.filter((one) => !ringed.has(one.fieldId)));
  const order = sorted.ok ? sorted.order : [];

  const out: Record<string, unknown> = { ...values };
  // One row per chosen id, in the person's order. A row this device has not
  // pulled holds nothing, and the total follows `onMissing` as for any empty field.
  // A `lines` field reads the lines handed in, and none when none were.
  const readLines: LinesReader = (linesFieldId) =>
    linesFields.has(linesFieldId)
      ? (childLines.get(linesFieldId) ?? [])
      : chosenIds(out[linesFieldId]).map((entryId) => rows.get(entryId) ?? {});
  for (const fieldId of ringed) out[fieldId] = null;
  for (const fieldId of order) {
    // `out`, not `values`: the answer just worked out is part of the picture
    // the next formula reads.
    out[fieldId] = evaluateFormula(held.get(fieldId)!, out as FormulaValues, readHop, readLines);
  }
  return out as EntryValues;
};

/**
 * True when anything in the tree works something out: the row itself, or a
 * line at any depth under it. A record with no formula of its own can still
 * hold lines that have one.
 */
export const treeHasFormulas = (schema: RecordSchemaResponse): boolean =>
  hasFormulas(schema) || Object.values(schema.lineSchemas ?? {}).some(treeHasFormulas);

/**
 * An entry and every line under it, with every formula answered, in the order
 * Decision 8 of docs/plans/entry-lines-and-layout.md fixes: the deepest lines
 * first, then the lines above them, then the entry. So an `agg` on a row only
 * ever totals lines that are already worked out, and a stay's Total guests sums
 * each room's own Guests count.
 *
 * `lines` is what the form holds, keyed by `lines` field id, each line a
 * `LineWrite` (its device-made id, its typed values, and its own lines). A
 * `lines` field the form does not hand in counts as none, so a form must hand
 * in every run it draws, edited or not.
 *
 * A line reads up the tree through its parent and root fields (Decision 6).
 * Neither row is in the mirror while the entry is unsaved, so each hop is
 * answered from the form's values (`lineContext`), and the two ids are taken
 * out again before the line is handed back. A line formula may read only the
 * TYPED fields above it (`FORMULA_PARENT_COMPUTED`), so the row above is read
 * as the person left it, and the order cannot ring.
 *
 * The same order runs on the server inside the write, and the server's answer
 * wins, as it does for a row with no lines.
 */
export const fillFormulaTree = (
  schema: RecordSchemaResponse,
  entry: RowAbove,
  lines: EntryLinesWrite,
): Readonly<{ values: EntryValues; lines: EntryLinesWrite }> => {
  const under = fillRuns(schema, lines, { parent: entry, root: entry });
  return {
    values: fillFormulas(schema, entry.values, { childLines: under.childLines }),
    lines: under.lines,
  };
};

/** Every run of lines under one row, worked out, and what each total reads. */
const fillRuns = (
  schema: RecordSchemaResponse,
  lines: EntryLinesWrite,
  above: LineAbove,
): Readonly<{ lines: EntryLinesWrite; childLines: ReadonlyMap<string, readonly FormulaValues[]> }> => {
  const lineSchemas = schema.lineSchemas ?? {};
  const out: Record<string, readonly LineWrite[]> = {};
  const childLines = new Map<string, readonly FormulaValues[]>();
  for (const [linesFieldId, run] of Object.entries(lines)) {
    const lineSchema = lineSchemas[linesFieldId];
    // A run whose schema this device does not hold (a cache from before it
    // existed) is handed back as typed, and totalled as it stands.
    const filled = lineSchema === undefined ? run : run.map((line) => fillLine(lineSchema, line, above));
    out[linesFieldId] = filled;
    childLines.set(linesFieldId, filled.map((line) => line.values as FormulaValues));
  }
  return { lines: out, childLines };
};

/** One line: its own lines first, then itself, reading up the tree from the form. */
const fillLine = (lineSchema: RecordSchemaResponse, line: LineWrite, above: LineAbove): LineWrite => {
  const own: RowAbove = { entryId: line.entryId, values: line.values };
  // Its own lines hang from it, and their root is the entry at the top.
  const under = fillRuns(lineSchema, line.lines ?? {}, { parent: own, root: above.root });
  const context = lineContext(lineSchema, line.values, above);
  const values = withoutLineRefs(
    lineSchema,
    fillFormulas(lineSchema, context.values, { known: context.known, childLines: under.childLines }),
  );
  return line.lines === undefined
    ? { entryId: line.entryId, values }
    : { entryId: line.entryId, values, lines: under.lines };
};

/**
 * The values a push may carry: everything a person typed, and no answer.
 *
 * The server works the answers out again from the recipe. Sending one would be
 * refused with FIELD_COMPUTED, which is the right refusal: an answer the device
 * could post is an answer the device could get wrong.
 */
export const withoutComputed = (
  schema: RecordSchemaResponse,
  values: EntryValues,
): EntryValues => {
  const computed = new Set(
    schema.fields
      .filter((field) => field.type === 'formula' || field.type === 'rollup' || field.type === 'lookup')
      .map((field) => field.id),
  );
  if (computed.size === 0) return values;
  const out: Record<string, unknown> = {};
  for (const [fieldId, value] of Object.entries(values)) {
    if (computed.has(fieldId)) continue;
    out[fieldId] = value;
  }
  return out as EntryValues;
};

/**
 * The lines a push may carry: what a person typed on each line, at both
 * levels, and no answer and no parent or root id.
 *
 * `withoutComputed` for a tree. The server works every line formula out again
 * inside the write, deepest first, and makes the parent and root of every
 * line itself (Decision 3), so a device that sent either would be sending a
 * value that is not its to send. A run whose schema the device does not hold
 * goes as it is: the server checks it against the schema it does hold.
 */
export const withoutComputedLines = (
  schema: RecordSchemaResponse,
  lines: EntryLinesWrite,
): EntryLinesWrite => {
  const lineSchemas = schema.lineSchemas ?? {};
  const out: Record<string, readonly LineWrite[]> = {};
  for (const [linesFieldId, run] of Object.entries(lines)) {
    const lineSchema = lineSchemas[linesFieldId];
    out[linesFieldId] =
      lineSchema === undefined
        ? run
        : run.map((line) => {
            const values = withoutLineRefs(lineSchema, withoutComputed(lineSchema, line.values));
            return line.lines === undefined
              ? { entryId: line.entryId, values }
              : { entryId: line.entryId, values, lines: withoutComputedLines(lineSchema, line.lines) };
          });
  }
  return out;
};
